import { FC, Fragment, useState } from 'react';
import {
	Button,
	Collapse,
	Divider,
	Form,
	Input,
	Select,
	Table,
	Typography,
} from 'antd';
import {
	FileSearchOutlined,
	EditOutlined,
	LoadingOutlined,
	ArrowLeftOutlined,
} from '@ant-design/icons';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	SubmitAgreementPrintingPayload,
	useGetAgreementPrintDetail,
	usePreviewAgreementPrintingMutation,
	usePreviewMultpleAgreementPrintingMutation,
	usePrintAgreementPrintingMutation,
	useSubmitAgreementPrintingMutation,
} from '@/libs/fleet-api/services/print';
import dayjs from 'dayjs';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import { useModals } from '@/components/modals/ModalProvider';
import { useApp } from '@/App';
import CovenantModal, { useCovenantModal } from './CovenantModal';
import { useUserData } from '@/libs/fleet-api/hooks';
import PreviewModal, {
	PreviewModalState,
} from '@/components/modals/PreviewModal';
import { formatCurrency, formatCurrencyWithDot } from '@/libs/antd/formater';
import { NAPNJFModal, useNAPNJFModal } from '../nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '../nap/NAPJFDownloadPage';
import { createAndDownloadZip } from '@/utils/zip-files';
import { getFileNameFromSignedUrl } from '@/utils/file-utils';

const PrintPage: FC<{ id: number; onBack?: () => void }> = ({ id, onBack }) => {
	const userData = useUserData();
	const { groupName } = userData ?? {};
	const { navigate } = useApp();
	const {
		modal,
		openPreviewModal,
		openCustomerGroupDetail,
		openCustomerDetail,
		openPreviewDocumentModal,
	} = useModals();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();

	const covenantModal = useCovenantModal();
	const [previewFile, setPreviewFile] = useState<PreviewModalState>(null);

	const { data, isFetching } = useGetAgreementPrintDetail(String(id));
	const {
		id: printId,
		prospectId,
		resolutionFile,
		napPlafonTypeId,
		customerName,
		customerId,
		napNo,
		bmppGroupName,
		bmppGroupId,
		industryClass,
		spfNo,
		spfId,
		napId,
		portfolioGuideline,
		ultimateOwner,
		rkpDate,
		resolutionFullApprovedDate,
		divisionName,
		frmName,
		fbdhName,
		approvalFacilityNotifications,
		facilities,
		covenants,
		covenantStatusOptions,
		creditDocumentReviewerOptions,
		approvalHistory,
		wayOfPaymentOptions,
		plafonTypeOptions,
		financingLimitTypeOptions,
	} = data?.data ?? {};
	// console.log({ printId });

	const { form, fieldName, value } = useForm<SubmitAgreementPrintingPayload>();

	const submitAgreementPrintingMutation = useSubmitAgreementPrintingMutation();
	const printAgreementPrintingMutation = usePrintAgreementPrintingMutation();
	const previewAgreementPrintingMutation =
		usePreviewAgreementPrintingMutation();
	const previewMultpleAgreementPrintingMutation =
		usePreviewMultpleAgreementPrintingMutation();

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 20,
			}}
		>
			<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
				<button
					style={{
						display: 'flex',
						height: 44,
						width: 44,
						fontSize: 24,
						border: 'none',
						alignItems: 'center',
						justifyContent: 'center',
						background: '#1557BC',
						color: 'white',
						cursor: 'pointer',
					}}
					onClick={() => onBack ? onBack() : navigate('PrintListPage')}
				>
					<ArrowLeftOutlined />
				</button>
				<FleetTitle style={{ width: '100%' }}>Main Information</FleetTitle>
			</div>
			<DataRow
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								'-'
							),
						],
						[
							'No. SPF',
							groupName === 'FCDVS' || (groupName === 'FCOH' && spfId) ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!spfId || !napId) {
											modal.confirm({
												title: 'Error',
												subTitle:
													'Data SPF tidak lengkap. Silakan refresh halaman dan coba lagi.',
												okText: 'OK',
											});
											return;
										}

										navigate('SPFPage', {
											prospectId: prospectId!,
											spfId: spfId,
											napResolutionId: napId,
											onBack: () => {
												navigate('PrintPage', {
													id,
												});
											},
											viewOnly: true,
										});
									}}
									style={{
										color: '#1677ff',
										textDecoration: 'underline',
									}}
								>
									{spfNo}
								</a>
							) : (
								spfNo ?? '-'
							),
						],
					],
					[
						[
							'Group Customer BMPP',
							bmppGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!bmppGroupId) return;
										openCustomerGroupDetail(bmppGroupId);
									}}
								>
									{bmppGroupName}
								</a>
							) : (
								'-'
							),
						],
						['Kelas Industri', industryClass ?? '-'],
					],
					[
						[
							'NAP ID',
							napNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) return;

										navigate('NAPPage', {
											prospectId,
											onBack: () => {
												navigate('PrintPage', {
													id,
												});
											},
										});
									}}
								>
									{napNo}
								</a>
							) : (
								'-'
							),
						],
						['Portofolio Guideline', portfolioGuideline ?? '-'],
					],
					[
						[
							'Summary NAP',
							napPlafonTypeId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) return;

										if (napPlafonTypeId === 1) {
											napNJFModal.open({
												prospectId,
											});
										}

										if (napPlafonTypeId === 2) {
											napJFModal.open({
												prospectId,
											});
										}
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
						['Ultimate Owner', ultimateOwner ?? '-'],
					],
					[
						[
							'Tanggal RKP',
							rkpDate ? dayjs(rkpDate).format('DD/MM/YYYY') : '-',
						],
						[
							'Tanggal Risalah Full Approve',
							resolutionFullApprovedDate
								? dayjs(resolutionFullApprovedDate).format('DD/MM/YYYY')
								: '-',
						],
					],
					[
						[
							'Summary Risalah',
							resolutionFile ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(resolutionFile);
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
						['Divisi', divisionName ?? '-'],
					],
					[
						['FRM', frmName ?? '-'],
						['FBDH', fbdhName ?? '-'],
					],
				]}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ span: 6 }}
				wrapperCol={{ span: 18 }}
				onFinish={async (values) => {
					if (groupName === 'FCOH') {
						const isConfirmed = await modal.confirm({
							title: 'Apakah data yang Anda inputkan sudah sesuai?',
							okText: 'YA',
							cancelText: 'TIDAK',
						});

						if (!isConfirmed) return;
						await submitAgreementPrintingMutation.mutateAsync(values);
						await modal.success({
							title: 'Sukses',
							subTitle: 'data berhasil di-submit',
							okText: 'BACK',
						});

						navigate('PrintListPage');
					}

					if (groupName === 'FCDVS') {
						try {
							if (!printId) {
								await modal.confirm({
									title: 'Error',
									subTitle:
										'Print ID tidak ditemukan. Silakan refresh halaman dan coba lagi.',
									okText: 'OK',
								});
								return;
							}

							const res =
								await previewMultpleAgreementPrintingMutation.mutateAsync({
									id: printId,
								});

							if (!res?.data?.length) {
								await modal.confirm({
									title: 'Error',
									subTitle:
										'Gagal menggenerate preview kontrak. Silakan coba lagi.',
									okText: 'OK',
								});
								return;
							}

							const fileURLs = res.data.map((d) => d.signedUrl);
							openPreviewDocumentModal({
								url: fileURLs,
								allowDownload: false,
								downloadAllDocuments: false,
								zipName: `Dokumen Perjanjian Pembiayaan - ${spfNo}`,
								button: {
									text: 'PRINT',
									onClick: async () => {
										const { data: agreementNumber } =
											await printAgreementPrintingMutation.mutateAsync({
												id: printId,
												notes: values.notes!,
											});

										if (!agreementNumber) {
											await modal.confirm({
												title: 'Error',
												subTitle:
													'Gagal membuat nomor perjanjian. Silakan coba lagi.',
												okText: 'OK',
											});
											return;
										}

										await modal.success({
											title: 'Perjanjian Pembiayaan Berhasil Dibuat!',
											subTitle: (
												<Fragment>
													<p>
														Berikut adalah nomor Perjanjian Pembiayaan Debitur
														Anda:
													</p>
													<b style={{ fontSize: 32, color: 'black' }}>
														{agreementNumber}
													</b>
												</Fragment>
											),
											okText: 'DOWNLOAD ALL',
											onOk: async () => {
												try {
													await createAndDownloadZip(
														fileURLs.map((d) => ({
															name: getFileNameFromSignedUrl(d),
															url: d,
														})),
														`Dokumen Perjanjian Pembiayaan - ${spfNo}`,
													);
												} catch (error) {
													console.error(
														'Failed to download all documents:',
														error,
													);
												}
											},
										});

										navigate('PrintListPage');
									},
								},
								buttonPosition: 'top',
							});
						} catch (error) {
							console.error('Error generating contract preview:', error);
							await modal.confirm({
								title: 'Error Preview Kontrak',
								subTitle:
									'Terjadi kesalahan saat menggenerate preview kontrak. Silakan coba lagi atau hubungi administrator.',
								okText: 'OK',
							});
						}
					}
				}}
			>
				<Form.Item
					name={fieldName('spfId')}
					initialValue={id}
					noStyle
				/>
				<Form.Item
					name={'id'}
					initialValue={printId}
					noStyle
				/>
				<Collapse
					defaultActiveKey={[0, 1]}
					items={[
						{
							label: 'Informasi Approval Pemberitahuan Fasilitas',
							children: (
								<Fragment>
									{approvalFacilityNotifications?.map((record, index) => (
										<Fragment key={index}>
											<div style={{ marginBottom: 10 }}>
												<strong>Limit Plafon {index + 1}</strong>
											</div>
											<Table
												bordered
												pagination={false}
												dataSource={
													(() => {
														const baseRows = [
															{
																subject: 'Jenis Pembiayaan',
																approval:
																	record.financingTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Jenis Fasilitas',
																approval:
																	record.facilityTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Limit Pembiayaan',
																approval: record.approvedLimit
																	? formatCurrency(record.approvedLimit)
																	: '-',
															},
															{
																subject: 'Sifat Limit Pembiayaan',
																approval:
																	financingLimitTypeOptions?.find(
																		(option) =>
																			option.id === record.financingLimitTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Jenis Plafon',
																approval:
																	plafonTypeOptions?.find(
																		(option) =>
																			option.id === record.plafonTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Way Of Payment',
																approval:
																	wayOfPaymentOptions?.find(
																		(option) =>
																			option.id === record.wayOfPaymentId,
																	)?.value ?? '-',
															},
															{
																subject: 'Masa Berlaku Fasilitas',
																approval: record.facilityValidityPeriod
																	? dayjs(record.facilityValidityPeriod).format(
																			'DD/MM/YYYY',
																		)
																	: '-',
															},
														];

														// Check if this is pembiayaan multiguna
														const isMultiguna = record.financingTypes?.some(
															(type) =>
																type.id === 3 ||
																type.value === 'Pembiayaan Multiguna',
														);

														// If pembiayaan multiguna, add 3 additional rows
														if (isMultiguna) {
															const additionalRows = [
																{
																	subject: 'Mekanisme Pencairan',
																	approval: record.disbursementMechanism ?? '-',
																},
																{
																	subject: 'Kondisi Objek Pembiayaan',
																	approval:
																		record.financingObjectCondition ?? '-',
																},
																{
																	subject: 'Penilaian Objek Pembiayaan',
																	approval:
																		record.financingObjectEvaluation ?? '-',
																},
															];
															return [...baseRows, ...additionalRows];
														}

														return baseRows;
													})() as Record<'subject' | 'approval', string>[]
												}
												columns={[
													{
														title: 'Perihal',
														width: '50%',
														render: (_, record) => record.subject,
													},
													{
														title: 'Keterangan Approval',
														width: '50%',
														render: (_, record) => <b>{record.approval}</b>,
													},
												]}
											/>
											<Divider />
										</Fragment>
									))}
									<Typography.Title level={3}>
										Detail Fasilitas
									</Typography.Title>
									<Table
										dataSource={facilities ?? []}
										rowKey="id"
										columns={[
											{
												title: 'No',
												align: 'center',
												render: (_, __, index: number) => index + 1,
											},
											{
												title: 'Jenis Asset',
												render: (_, record) => record.assetType?.value,
											},
											{
												title: 'Kondisi Obyek Pembiayaan',
												render: (_, record) =>
													record.financingObjectCondition?.value,
											},
											{
												title: 'Sifat Limit Pembiayaan',
												render: (_, record) =>
													record.financingLimitType?.value ?? '-',
											},
											{
												title: 'Jenis Plafon',
												render: (_, record) => record.plafonType?.value,
											},
											{
												title: 'Tenor Pembiayaan',
												render: (_, record) => record.tenor?.value,
											},
											{
												title: 'Indikasi Suku Bunga (Eff)',
												render: (_, record) =>
													record.interestRateIndicationPercent
														? `${formatCurrencyWithDot(record.interestRateIndicationPercent)}%`
														: '-',
											},
											{
												title: 'Down Payment',
												render: (_, record) =>
													record.downPaymentPercent
														? `${formatCurrencyWithDot(record.downPaymentPercent)}%`
														: '-',
											},
											{
												title: 'Security Deposit',
												render: (_, record) =>
													record.securityDeposit
														? formatCurrency(record.securityDeposit)
														: '-',
											},
											{
												title: 'Biaya Administrasi',
												render: (_, record) =>
													record.administrativeFee
														? formatCurrency(record.administrativeFee)
														: '-',
											},
											{
												title: 'Provisi',
												render: (_, record) =>
													record.provisionFee
														? formatCurrencyWithDot(record.provisionFee) + '%'
														: '-',
											},
											{
												title: 'Biaya Layanan',
												render: (_, record) =>
													record.serviceFee
														? `${formatCurrencyWithDot(record.serviceFee)}%`
														: '-',
											},
											{
												title: 'Asuransi',
												render: (_, record) => record.insurance?.value ?? '-',
											},
											{
												title: 'Diskon Asuransi',
												render: (_, record) =>
													record.insuranceDiscountPercentage
														? `${formatCurrencyWithDot(record.insuranceDiscountPercentage)}%`
														: '-',
											},
											{
												title: 'Fidusia',
												render: (_, record) =>
													record.fiduciaryFee
														? formatCurrency(record.fiduciaryFee)
														: '-',
											},
										]}
										bordered
										pagination={false}
										scroll={{ x: 'max-content' }}
									/>
								</Fragment>
							),
						},
						{
							label: 'Informasi Dokumen Covenant',
							children: (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
									}}
								>
									<Form.Item
										name={fieldName('covenants')}
										wrapperCol={{ span: 24 }}
										rules={[
											{
												validator: (
													_,
													covenants: SubmitAgreementPrintingPayload['covenants'],
												) => {
													if (groupName === 'FCOH') {
														// Ensure covenants array exists and is valid
														if (!Array.isArray(covenants)) {
															return Promise.reject(
																'Data covenant tidak valid. Silakan refresh halaman.',
															);
														}

														// Check if there are any covenants with documents
														const covenantsWithDocuments = covenants.filter(
															(covenant) =>
																covenant &&
																covenant.covenantDocument?.id &&
																typeof covenant.covenantDocument === 'object',
														);

														if (covenantsWithDocuments.length > 0) {
															// For each covenant with document, both statusId and notes are required
															const invalidCovenants =
																covenantsWithDocuments.filter(
																	(covenant) =>
																		!covenant.statusId ||
																		!covenant.notes ||
																		covenant.statusId.trim() === '' ||
																		covenant.notes.trim() === '',
																);

															if (invalidCovenants.length > 0) {
																const count = invalidCovenants.length;
																const message =
																	count === 1
																		? 'Ada 1 covenant yang sudah diupload dokumennya namun belum diisi status dan keterangannya lengkap. Silakan lengkapi data covenant terlebih dahulu.'
																		: `Ada ${count} covenant yang sudah diupload dokumennya namun belum diisi status dan keterangannya lengkap. Silakan lengkapi data covenant terlebih dahulu.`;

																return Promise.reject(message);
															}
														}
													}
													return Promise.resolve();
												},
											},
										]}
										initialValue={
											covenants?.map((covenant) => {
												return {
													id: covenant.id,
													dueDate: covenant.dueDate,
													statusId: covenant.status,
													notes: covenant.notes,
													covenantDocument: covenant.covenantDocument,
												} as SubmitAgreementPrintingPayload['covenants'][number];
											}) ?? []
										}
									>
										<Table
											dataSource={value?.covenants}
											rowKey="id"
											columns={[
												{
													title: 'No',
													align: 'center',
													render: (_, __, index) => index + 1,
												},
												{
													title: 'Edit',
													hidden: groupName === 'FCDVS',
													render: (_, record, recordIndex) => {
														if (!record.covenantDocument?.id) {
															return (
																<EditOutlined style={{ color: '#bababa' }} />
															);
														}
														return (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const detail = covenants?.find(
																		(covenant) => covenant.id === record.id,
																	);
																	const updatedCovenant =
																		await covenantModal.open({
																			record,
																			detail,
																		});

																	if (!updatedCovenant) return;
																	form.setFieldValue(
																		fieldName('covenants'),
																		value?.covenants?.map(
																			(covenant, covenantIndex) => {
																				return recordIndex === covenantIndex
																					? updatedCovenant
																					: covenant;
																			},
																		) ?? [],
																	);
																}}
															>
																<EditOutlined />
															</a>
														);
													},
												},
												{
													title: 'Covenant',
													width: 200,
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.covenant ?? '-';
													},
												},
												{
													title: 'Kategori',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.category?.value ?? '-';
													},
												},
												{
													title: 'Priority',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.priority?.value ?? '-';
													},
												},
												{
													title: 'Repeat',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.repeatFrequency?.value ?? '-';
													},
												},
												{
													title: 'Jenis Monitoring',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.monitoringType?.value ?? '-';
													},
												},
												{
													title: 'Analisa Lebih Lanjut',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.furtherAnalysis?.value ?? '-';
													},
												},
												{
													title: 'Tanggal Upload Dokumen',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);
														return record?.uploadDocumentDate
															? dayjs(record.uploadDocumentDate).format(
																	'DD/MM/YYYY',
																)
															: '-';
													},
												},
												{
													title: 'View Dokumen',
													align: 'center',
													fixed: 'right',
													render: (_, { id }) => {
														const record = covenants?.find(
															(covenant) => covenant.id === id,
														);

														return (
															<a
																href="#"
																style={
																	!record?.covenantDocument?.id
																		? {
																				color: '#bababa',
																				cursor: 'not-allowed',
																			}
																		: {}
																}
																onClick={(e) => {
																	e.preventDefault();

																	if (!record?.covenantDocument?.id) return;
																	openPreviewModal(record.covenantDocument);
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'Status',
													render: (_, { statusId }) => {
														return statusId
															? statusId[0].toUpperCase() + statusId.slice(1)
															: '-';
													},
												},
												{
													title: 'Due Date',
													render: (_, record) =>
														record.dueDate
															? dayjs(record.dueDate).format('DD/MM/YYYY')
															: '-',
												},
												{
													title: 'OD',
													render: (_, { id }) => {
														const od = covenants?.find(
															(covenant) => covenant.id === id,
														)?.overdueDays;

														return typeof od === 'number' ? `${od} Hari` : '-';
													},
												},
												{
													title: 'Keterangan',
													render: (_, record) => record.notes ?? '-',
												},
											]}
											bordered
											pagination={false}
											scroll={{ x: 'max-content' }}
										/>
									</Form.Item>

									{groupName === 'FCOH' && (
										<Fragment>
											<FleetTitle>
												Assign Credit Data Entry & Credit Document Reviewer
											</FleetTitle>

											<Form.Item
												name={fieldName('creditDocumentReviewerId')}
												label="Pilih Credit Document Reviewer"
												rules={[rule.required()]}
											>
												<Select
													options={creditDocumentReviewerOptions?.map(
														(option) => ({
															label: option.value,
															value: option.id,
														}),
													)}
													style={{ width: 200 }}
												/>
											</Form.Item>

											<Divider style={{ margin: 0 }} />

											<Form.Item
												label="Notes"
												name={fieldName('notes')}
												rules={[rule.required()]}
											>
												<Input.TextArea rows={3} />
											</Form.Item>

											<Button
												type="primary"
												htmlType="submit"
												disabled={submitAgreementPrintingMutation.isPending}
												loading={submitAgreementPrintingMutation.isPending}
												style={{
													width: 200,
													marginLeft: 'auto',
												}}
											>
												SAVE
											</Button>
										</Fragment>
									)}

									{groupName === 'FCDVS' && (
										<Fragment>
											<FleetTitle>Approval History</FleetTitle>

											<Table
												bordered
												pagination={false}
												dataSource={approvalHistory?.map((history, index) => ({
													...history,
													key: index,
												}))}
												columns={[
													{
														title: 'No',
														align: 'center',
														render: (_, __, index) => index + 1,
													},
													{
														title: 'Name',
														render: (_, record) => record.name,
													},
													{
														title: 'Date',
														align: 'center',
														render: (_, record) =>
															record.date &&
															dayjs(record.date).format('DD/MM/YYYY'),
													},
													{
														title: 'Process',
														align: 'center',
														render: (_, record) => record.process,
													},
													{
														title: 'Status',
														align: 'center',
														render: (_, record) => record.status,
													},
													{
														title: 'Approval Notes',
														align: 'center',
														render: (_, record) => record.notes,
													},
												]}
											/>
											<Divider style={{ margin: 0 }} />

											<Form.Item
												label="Notes"
												name={fieldName('notes')}
												rules={[rule.required()]}
											>
												<Input.TextArea rows={3} />
											</Form.Item>

											<Button
												type="primary"
												htmlType="submit"
												style={{
													width: 200,
													marginLeft: 'auto',
												}}
												disabled={previewAgreementPrintingMutation.isPending}
												loading={previewAgreementPrintingMutation.isPending}
											>
												CETAK KONTRAK
											</Button>
										</Fragment>
									)}
								</div>
							),
						},
					]}
				/>
			</Form>
			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			<CovenantModal
				{...covenantModal}
				covenantStatusOptions={covenantStatusOptions ?? []}
			/>
			<PreviewModal
				file={previewFile}
				onClose={() => setPreviewFile(null)}
				action={
					<div
						style={{
							position: 'absolute',
							inset: 'auto 0 40px 0',
							display: 'flex',
							justifyContent: 'center',
							gap: 20,
						}}
					>
						<Button
							type="primary"
							download
							href={previewFile?.url}
							target="_blank"
							style={{
								width: 200,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							DOWNLOAD
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							style={{
								width: 200,
							}}
							onClick={async () => {
								try {
									// setPreviewFile(null);
									const values = await form.validateFields();

									if (!printId) {
										await modal.confirm({
											title: 'Error',
											subTitle:
												'Print ID tidak ditemukan. Silakan refresh halaman dan coba lagi.',
											okText: 'OK',
										});
										return;
									}

									if (!values.notes) {
										await modal.confirm({
											title: 'Error',
											subTitle: 'Notes harus diisi sebelum mencetak kontrak.',
											okText: 'OK',
										});
										return;
									}

									const { data: agreementNumber } =
										await printAgreementPrintingMutation.mutateAsync({
											id: printId,
											notes: values.notes,
										});

									if (!agreementNumber) {
										await modal.confirm({
											title: 'Error',
											subTitle:
												'Gagal membuat nomor perjanjian. Silakan coba lagi.',
											okText: 'OK',
										});
										return;
									}

									await modal.success({
										title: 'Perjanjian Pembiayaan Berhasil Dibuat!',
										subTitle: (
											<Fragment>
												<p>
													Berikut adalah nomor Perjanjian Pembiayaan Debitur
													Anda:
												</p>
												<b style={{ fontSize: 32, color: 'black' }}>
													{agreementNumber}
												</b>
											</Fragment>
										),
										okText: 'DOWNLOAD',
										okButtonProps: {
											download: true,
											href: previewFile?.url,
											target: '_blank',
											style: {
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											},
										},
									});

									navigate('PrintListPage');
								} catch (error) {
									console.error('Error printing contract:', error);
									await modal.confirm({
										title: 'Error Mencetak Kontrak',
										subTitle:
											'Terjadi kesalahan saat mencetak kontrak. Silakan coba lagi atau hubungi administrator.',
										okText: 'OK',
									});
								}
							}}
						>
							PRINT
						</Button>
					</div>
				}
			/>
		</div>
	);
};

export default PrintPage;
